"use client"

import { ArrowRight } from "lucide-react"
import { indexVar } from "@/components/marketing/landing-v2/cssVars"
import { Mascot } from "@/components/marketing/landing-v2/Mascot"
import type { Landing2Copy } from "@/lib/landing-v2/copy"

/**
 * The hero visual: a problem arrives in the visitor's own words, the agent picks
 * it up, and three stages report back. Same node/status language as the rails
 * further down the page, at hero scale.
 */
function AgentRuntime({ copy }: { copy: Landing2Copy }) {
  const runtime = copy.hero.runtime

  return (
    <div className="l2-runtime">
      <div className="l2-runtime-bar">
        <span className="l2-runtime-title">{runtime.title}</span>
        <span className="l2-status">
          <i />
          {runtime.status}
        </span>
      </div>

      <div className="l2-runtime-body">
        <div className="l2-runtime-input">
          <span className="l2-label">{runtime.inputLabel}</span>
          <div className="l2-runtime-quotes">
            {runtime.problems.map((problem, index) => (
              <p key={problem} className="l2-runtime-quote" style={indexVar(index)}>
                {problem}
              </p>
            ))}
          </div>
        </div>

        <span className="l2-runtime-spine" aria-hidden="true">
          <i className="l2-runtime-spark" />
        </span>

        <div className="l2-runtime-core">
          <span className="l2-runtime-glow" aria-hidden="true" />
          <Mascot size={112} alt="Atirame" priority className="l2-runtime-mascot" />
        </div>

        <ul className="l2-runtime-stages">
          {runtime.stages.map((stage, index) => (
            <li key={stage.label} className="l2-runtime-stage" style={indexVar(index)}>
              <i className="l2-runtime-stage-dot" aria-hidden="true" />
              <span className="l2-runtime-stage-label">{stage.label}</span>
              <span className="l2-runtime-stage-status">{stage.status}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function Landing2Hero({
  copy,
  onCta,
}: {
  copy: Landing2Copy
  onCta: () => void
}) {
  return (
    <section className="l2-hero" id="top">
      <span className="l2-hero-field" aria-hidden="true" />

      <div className="l2-container l2-hero-grid">
        <div className="l2-hero-copy">
          <h1 className="l2-display">
            <span className="l2-display-line" style={indexVar(0)}>
              {copy.hero.lineOne}
            </span>
            <span className="l2-display-line" style={indexVar(1)}>
              {copy.hero.lineTwo}
            </span>
          </h1>

          <p className="l2-hero-lead" style={indexVar(2)}>
            {copy.hero.lead}
          </p>

          <div className="l2-hero-actions" style={indexVar(3)}>
            <button type="button" className="l2-button l2-button-lg" onClick={onCta}>
              {copy.nav.cta}
              <ArrowRight size={17} strokeWidth={2.2} />
            </button>
            <a className="l2-text-link" href="#modules">
              {copy.hero.ctaSecondary}
              <ArrowRight size={14} strokeWidth={2} />
            </a>
          </div>

          <p className="l2-hero-note" style={indexVar(4)}>
            {copy.hero.note}
          </p>
        </div>

        <div className="l2-hero-visual" style={indexVar(2)}>
          <AgentRuntime copy={copy} />
        </div>
      </div>
    </section>
  )
}
